import React, { useContext, useEffect, useState } from "react";
import { toAbsoluteUrl } from "../../_metronic/_helpers/AssetsHelpers";
import { DetailsMapService } from "../../pages/services/DetailsMapService";
import { PositionElement } from "../../pages/tripDetails/components/PositionElement";
import ReactDOMServer from "react-dom/server";
import { useTripContext } from "../../context/tripsContext";
import MapLegend from "../../pages/tripDetails/components/MapLegend";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "../../utils/hooks/useWindowSize";

interface Position {
  latitude: number;
  longitude: number;
}

interface MapViewFullScreenProps {
  visible: boolean;
  fullScreenHandler: () => void;
  positions: Position[];
}

const MapViewFullScreen: React.FC<MapViewFullScreenProps> = ({
  visible,
  fullScreenHandler,
  positions,
}) => {
  const { stops } = useTripContext();
  const [openLegend, setOpenLegend] = useState(false);
  const { t } = useTranslation();

  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 1226;
  const calculateCenterMap = (
    points: { latitude: number; longitude: number }[]
  ) => {
    const total = points.length;
    if (total === 0) return { latitude: 0, longitude: 0 };

    const sum = points.reduce(
      (acc, point) => ({
        latitude: acc.latitude + point.latitude,
        longitude: acc.longitude + point.longitude,
      }),
      { latitude: 0, longitude: 0 }
    );

    return {
      latitude: sum.latitude / total,
      longitude: sum.longitude / total,
    };
  };

  useEffect(() => {
    if (visible) {
      const H = (window as any).H;
      if (!H) {
        console.error("HERE Maps library is not loaded.");
        return;
      }

      const mapTileType = "logistics.day";

      const actualPosition = positions[positions.length - 1]; // Ultimo punto

      // Filtra l'array per rimuovere l'actualPosition
      const filteredPositions = positions.slice(0, positions.length - 1);

      // Unire positions e stops per calcolare il centro della mappa
      const allPoints = [
        ...positions,
        ...stops.map((stop) => ({
          latitude: stop.latitude,
          longitude: stop.longitude,
        })),
      ];

      const center = calculateCenterMap(allPoints);

      // Inizializza la mappa
      const mapResult = DetailsMapService.createMap(
        "mapContainer",
        { lat: center?.latitude, lng: center?.longitude },
        9,
        mapTileType
      );

      if (!mapResult) {
        console.error("Failed to create map.");
        return;
      }

      const { map, ui } = mapResult;

      if (filteredPositions.length > 0) {
        // Aggiungi il marker per actualPosition
        DetailsMapService.markers.addActualPositionMarker(
          map,
          actualPosition,
          ui
        );

        //Aggiunge le positions clusterizzati
        DetailsMapService.startClustering(filteredPositions, map, ui);

        // Punti per disegnare il percorso
        const gpsTelematics = positions.map((position) => ({
          lat: position.latitude,
          lng: position.longitude,
        }));

        // Disegna il percorso
        DetailsMapService.renderActualViewRoute(map, gpsTelematics);
      }

      //Aggiunge gli stop
      stops.forEach((stop) => {
        DetailsMapService.markers.addStopMarker(
          map,
          {
            latitude: stop.latitude,
            longitude: stop.longitude,
          },
          ui,
          stop
        );
      });

      return () => {
        map.dispose();
      };
    }
  }, [visible, positions]);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 bg-white box-border z-[99999] rounded-lg ${isMobile
          ? "h-[100%] w-[100%] my-0 mx-0"
          : "h-[94%] w-[98%] my-10 mx-5"
        }`}
    >

      {/* Contenitore della mappa */}
      <div id="mapContainer" className="w-full h-full rounded-lg"></div>

      {/* MapLegend */}
      <div
        className="absolute top-4 left-4 flex z-[1] flex-row justify-center items-center py-[10px] px-[12px] w-[91px] h-[44px] cursor-pointer bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] rounded-[2px]"
        onClick={() => setOpenLegend(!openLegend)}
      >
        <div className="w-[45px] h-[14px] font-semibold text-[12px] leading-[14px] text-black">
          {t("screens.trips.mapLegend.Legend")}
        </div>
        <div
          className={`legend-arrow flex items-center transition-transform duration-200 ml-1 ${openLegend ? "rotate-180" : ""
            }`}
        >
          <img
            src={toAbsoluteUrl("/media/svg/map/arrow-down.svg")}
            alt=""
            className="w-4 h-4"
          />
        </div>
        {openLegend && (
          <MapLegend showIconLeft={true} openTrackingPosition={false} />
        )}
      </div>

      {/* Pulsante per chiudere */}
      <div
        className="absolute top-4 right-4 flex flex-col items-end space-y-2 rounded-lg cursor-pointer"
        onClick={fullScreenHandler}
      >
        <img
          src={toAbsoluteUrl("/media/svg/map/fullscreen.svg")}
          alt="Fullscreen"
        />
      </div>
    </div>
  );
};

export default MapViewFullScreen;
